import { useEffect, useMemo, useState } from 'react'
import { Download, Loader2 } from 'lucide-react'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { resolveStageLabel, resolvePatronListTitle, resolvePatronListSecondaryId } from '../../utils/workspaceCaseStage'

const P = {
  pri: '#0a5169',
  priM: '#e8f2f6',
  ink: '#0f172a',
  slate: '#64748b',
  slateL: '#94a3b8',
  border: '#e2e8f0',
  cardBg: '#ffffff',
  pageBg: '#f6f9fb',
  skipped: '#94a3b8',
}

function cellKindStyle(kind) {
  if (kind === 'skipped') return { color: P.skipped, fontStyle: 'italic' }
  if (kind === 'not_reached') return { color: P.slateL }
  if (kind === 'in_stage' || kind === 'opened') return { color: P.slate }
  return { color: P.ink }
}

export default function InvestigationAuditTrailView({
  caseIds = [],
  cases = [],
  stageLabels = {},
  onOpenCase,
  canExport = false,
  exportQuery = '',
}) {
  const [trail, setTrail] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [exporting, setExporting] = useState(false)

  const idsKey = useMemo(() => caseIds.map(String).join(','), [caseIds])

  useEffect(() => {
    if (!idsKey) {
      setTrail({ stages: [], data: [] })
      return
    }
    let cancelled = false
    ;(async () => {
      setLoading(true)
      setError(null)
      try {
        const res = await authFetch(getLocalApiUrl(`/api/cases/audit-trail?case_ids=${encodeURIComponent(idsKey)}`))
        const json = await res.json()
        if (cancelled) return
        if (!json.success) throw new Error(json.error || 'Failed to load audit trail')
        setTrail(json)
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load audit trail')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [idsKey])

  const stages = trail?.stages || []
  const caseById = useMemo(() => {
    const m = new Map()
    for (const c of cases) {
      if (c?.id != null) m.set(String(c.id), c)
    }
    return m
  }, [cases])
  const rows = useMemo(() => (trail?.data || []).map((row) => {
    const src = caseById.get(String(row.case_id))
    if (!src) return row
    return {
      ...row,
      patron_name: resolvePatronListTitle(src) || row.patron_name,
      patron_secondary_id: resolvePatronListSecondaryId(src) || row.patron_secondary_id,
    }
  }), [trail, caseById])

  const handleExport = async () => {
    if (!canExport || exporting) return
    setExporting(true)
    try {
      const qs = exportQuery || (idsKey ? `case_ids=${encodeURIComponent(idsKey)}` : '')
      const res = await authFetch(getLocalApiUrl(`/api/cases/audit-trail.xlsx${qs ? `?${qs}` : ''}`))
      if (!res.ok) {
        let msg = `Export failed (${res.status})`
        try {
          const json = await res.json()
          if (json?.error) msg = json.error
        } catch { /* binary */ }
        throw new Error(msg)
      }
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      const day = new Date().toISOString().slice(0, 10)
      a.href = url
      a.download = `investigation-audit-trail-${day}.xlsx`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (e) {
      setError(e.message || 'Export failed')
    } finally {
      setExporting(false)
    }
  }

  if (loading && !trail) {
    return (
      <div className="rounded-xl border p-10 text-center text-sm" style={{ borderColor: P.border, background: P.cardBg, color: P.slate }}>
        Loading audit trail…
      </div>
    )
  }

  if (error && !trail?.data?.length) {
    return (
      <div className="rounded-xl border p-10 text-center text-sm" style={{ borderColor: P.border, background: P.cardBg, color: '#dc2626' }}>
        {error}
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm m-0 leading-relaxed" style={{ color: P.slate }}>
          One row per investigation. Each stage column is the move into that stage — who, the note, and the checklist sentences they ticked. Older moves show “Checklist: not recorded”.
        </p>
        {canExport && (
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting || !caseIds.length}
            className="inline-flex items-center gap-2 text-sm font-bold px-4 py-2.5 rounded-lg border shrink-0"
            style={{ borderColor: P.pri, background: P.priM, color: P.pri, opacity: exporting ? 0.6 : 1 }}
          >
            {exporting ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
            {exporting ? 'Exporting…' : 'Export Excel'}
          </button>
        )}
      </div>
      {error && trail?.data?.length > 0 && (
        <div className="text-xs" style={{ color: '#dc2626' }}>{error}</div>
      )}
      <div className="rounded-xl border overflow-auto" style={{ borderColor: P.border, background: P.cardBg, maxHeight: 'calc(100vh - 16rem)' }}>
        <table className="text-left" style={{ minWidth: 1100, width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
          <thead>
            <tr>
              <th className="sticky top-0 left-0 z-40 text-[10px] font-extrabold uppercase tracking-wider px-3 py-2.5 whitespace-nowrap" style={{ color: P.slateL, background: P.pageBg, minWidth: 140, boxShadow: `inset 0 -1px 0 ${P.border}, inset -1px 0 0 ${P.border}` }}>
                Investigation
              </th>
              <th className="sticky top-0 z-40 text-[10px] font-extrabold uppercase tracking-wider px-3 py-2.5 whitespace-nowrap" style={{ color: P.slateL, background: P.pageBg, left: 140, minWidth: 160, boxShadow: `inset 0 -1px 0 ${P.border}, inset -1px 0 0 ${P.border}` }}>
                Patron
              </th>
              <th className="sticky top-0 z-30 text-[10px] font-extrabold uppercase tracking-wider px-3 py-2.5 whitespace-nowrap" style={{ color: P.slateL, background: P.pageBg, minWidth: 120, boxShadow: `inset 0 -1px 0 ${P.border}` }}>
                Current
              </th>
              {stages.map((s) => (
                <th key={s.id} className="sticky top-0 z-30 text-[10px] font-extrabold uppercase tracking-wider px-3 py-2.5 whitespace-nowrap" style={{ color: P.slateL, background: P.pageBg, minWidth: 220, boxShadow: `inset 0 -1px 0 ${P.border}` }}>
                  {resolveStageLabel(s.id, stageLabels) || s.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.case_id} className="align-top">
                <td className="sticky left-0 z-10 px-3 py-3 border-b border-r text-xs font-bold" style={{ borderColor: P.border, background: P.cardBg, color: P.pri, minWidth: 140 }}>
                  <button
                    type="button"
                    onClick={() => onOpenCase?.(row.case_number || row.case_id)}
                    className="bg-transparent border-0 p-0 cursor-pointer font-bold text-left"
                    style={{ color: P.pri, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
                  >
                    {row.case_number}
                  </button>
                </td>
                <td className="sticky z-10 px-3 py-3 border-b border-r text-xs" style={{ borderColor: P.border, background: P.cardBg, left: 140, minWidth: 160, color: P.ink }}>
                  <div className="font-semibold">{row.patron_name || '—'}</div>
                  {row.patron_secondary_id ? (
                    <div className="text-[11px] mt-0.5" style={{ color: P.slateL }}>{row.patron_secondary_id}</div>
                  ) : null}
                </td>
                <td className="px-3 py-3 border-b text-xs font-semibold whitespace-nowrap" style={{ borderColor: P.border, color: P.pri }}>
                  {resolveStageLabel(row.current_stage, stageLabels) || row.current_stage_label}
                </td>
                {stages.map((s) => {
                  const cell = row.cells?.[s.id] || { kind: 'not_reached', text: '' }
                  return (
                    <td
                      key={s.id}
                      className="px-3 py-3 border-b text-[11px] leading-relaxed whitespace-pre-wrap"
                      style={{ borderColor: P.border, minWidth: 220, ...cellKindStyle(cell.kind) }}
                    >
                      {cell.text || (cell.kind === 'not_reached' ? '' : '—')}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
